import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { QueryParams } from './query-params';
import { Observable, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { Body, ProductQuery } from '@/app/models/body';
import InterfaceHttpService from '@/app/core/http/http-service.interface';
import { RequestCancellationService } from './request-cancellation.service';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';

@Injectable({
  providedIn: 'root',
})
export class SimpleRdoHttpService implements InterfaceHttpService {
  protected shouldAbort: boolean = true;
  protected cancelSubject$: Subject<any> | null = null;

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private config: ConfigService,
    private cancellationService: RequestCancellationService,
    private featureFlagService: FeatureFlagService
  ) {}

  /**
   * Gets the route cancellation subject for use in wrapper services (e.g., XHR requests).
   * @internal Used by RdoHttpService for XHR cancellation.
   */
  public getRouteCancellationSubject(): Subject<void> | undefined {
    return this.cancellationService.getRouteCancellationSubject(
      this.shouldAbort
    );
  }

  /**
   * Disables abort for all requests on this instance.
   * Use with clone() for isolation: service.clone().disableRoutingAbort().get(...)
   */
  public disableRoutingAbort(): InterfaceHttpService {
    this.shouldAbort = false;
    return this;
  }

  public setCancelSubject(sub$: Subject<any>): InterfaceHttpService {
    this.cancelSubject$ = sub$;
    return this;
  }

  /**
   * Creates a new instance of SimpleRdoHttpService with the same dependencies.
   * This allows you to call disableRoutingAbort() on the cloned instance
   * without affecting other requests using the original singleton service.
   *
   * @returns A new SimpleRdoHttpService instance
   *
   * @example
   * this.simpleRdoHttpService.clone().disableRoutingAbort().get('/endpoint')
   */
  public clone(): SimpleRdoHttpService {
    return new SimpleRdoHttpService(
      this.http,
      this.authenticationService,
      this.config,
      this.cancellationService,
      this.featureFlagService
    );
  }

  /**
   * Returns cancellation subject based on shouldAbort flag and any custom cancel subject.
   */
  private getCancelSubject(): Subject<any> | undefined {
    return this.cancellationService.getCancellationSubject(
      this.shouldAbort,
      this.cancelSubject$
    );
  }

  public delete = (...args: any[]): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        const endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};
        let searchParams = new HttpParams();
        if (args.length > 1 && args[1]) {
          searchParams = this.toHttpParams(args[1].search as QueryParams);
        }
        return this.config.baseApiUrl.pipe(
          switchMap((apiUrl) => {
            const url = apiUrl + endpoint;
            const headers = this.getHeaders(token.AccessToken);
            const obs = this.http.delete(url, {
              headers,
              params: searchParams,
              withCredentials: true,
            });
            return cancel$
              ? obs.pipe(
                  takeUntil(cancel$),
                  tap({
                    complete: () => {
                      if (cancel$.isStopped || cancel$.observers.length === 0) {
                        console.warn(`[HTTP CANCELLED]: DELETE ${url}`);
                      }
                    },
                  })
                )
              : obs;
          })
        );
      })
    );
  };

  public get = (...args: any[]): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        let endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};
        let searchParams = new HttpParams();

        if (args.length > 1 && args[1]) {
          searchParams = this.toHttpParams(args[1].search as QueryParams);
        }

        if (
          this.authenticationService &&
          this.authenticationService._userInfoView &&
          this.authenticationService._userInfoView.SelectedClient
        ) {
          let delimit = '?';
          if (endpoint.toLowerCase().indexOf('clientid=') === -1) {
            if (endpoint.indexOf('?') > -1) {
              delimit = '&';
            }
            endpoint =
              endpoint +
              '?' +
              'ClientId=' +
              this.authenticationService._userInfoView.SelectedClient.ClientID.toString();
          }
        }

        return this.config.baseApiUrl.pipe(
          switchMap((apiUrl) => {
            if (token?.AccessToken) {
              const url = apiUrl + endpoint;
              const headers = this.getHeaders(token.AccessToken);

              const obs = this.http.get(url, {
                headers,
                params: searchParams,
                withCredentials: true,
              });
              return cancel$
                ? obs.pipe(
                    takeUntil(cancel$),
                    tap({
                      complete: () => {
                        if (
                          cancel$.isStopped ||
                          cancel$.observers.length === 0
                        ) {
                          console.warn(`[HTTP CANCELLED]: GET ${url}`);
                        }
                      },
                    })
                  )
                : obs;
            }
            return new Observable<any>();
          })
        );
      })
    );
  };

  public patch = (...args: any[]): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        const endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};

        return this.config.baseApiUrl.pipe(
          switchMap((apiUrl) => {
            const url = apiUrl + endpoint;
            const headers = this.getHeaders(token.AccessToken);

            const obs = this.http.patch(url, args[1], {
              withCredentials: true,
              headers,
            });
            return cancel$
              ? obs.pipe(
                  takeUntil(cancel$),
                  tap({
                    complete: () => {
                      if (cancel$.isStopped || cancel$.observers.length === 0) {
                        console.warn(`[HTTP CANCELLED]: PATCH ${url}`);
                      }
                    },
                  })
                )
              : obs;
          })
        );
      })
    );
  };

  public put = (...args: any[]): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        const endpoint = args[0].indexOf('/') === 0 ? args[0] : '/' + args[0];
        args[1] = args[1] || {};

        return this.config.baseApiUrl.pipe(
          switchMap((apiUrl) => {
            const url = apiUrl + endpoint;
            const headers = this.getHeaders(token.AccessToken);
            const obs = this.http.put(url, args[1], {
              headers,
              withCredentials: true,
            });
            return cancel$
              ? obs.pipe(
                  takeUntil(cancel$),
                  tap({
                    complete: () => {
                      if (cancel$.isStopped || cancel$.observers.length === 0) {
                        console.warn(`[HTTP CANCELLED]: PUT ${url}`);
                      }
                    },
                  })
                )
              : obs;
          })
        );
      })
    );
  };

  public post = (
    endpoint: string,
    content?: Body
  ): Observable<Record<any, any>> => {
    const cancel$ = this.getCancelSubject();

    endpoint = endpoint.indexOf('/') !== 0 ? '/' + endpoint : endpoint;
    const postBody: any = content || new ProductQuery();

    return this.authenticationService.getAuthToken().pipe(
      switchMap((token) => {
        if (token && token.AccessToken) {
          if (
            postBody.ClientId === undefined &&
            this.authenticationService &&
            this.authenticationService._userInfoView &&
            this.authenticationService._userInfoView.SelectedClient
          ) {
            postBody.ClientId =
              this.authenticationService._userInfoView.SelectedClient.ClientID.toString();
          }
          const headers = this.getHeaders(token.AccessToken);
          return this.config.baseApiUrl.pipe(
            switchMap((apiUrl) => {
              const url = apiUrl + endpoint;
              const obs = this.http.post(url, postBody, {
                headers,
                withCredentials: true,
              });
              return cancel$
                ? obs.pipe(
                    takeUntil(cancel$),
                    tap({
                      complete: () => {
                        if (
                          cancel$.isStopped ||
                          cancel$.observers.length === 0
                        ) {
                          console.warn(`[HTTP CANCELLED]: POST ${url}`);
                        }
                      },
                    })
                  )
                : obs;
            })
          );
        }
        return new Observable<any>();
      })
    );
  };

  public toHttpParams = (params: QueryParams): HttpParams => {
    if (!params) {
      return new HttpParams();
    }
    let httpParams = new HttpParams();
    Array.from(params.paramsMap.entries()).forEach((entry) => {
      if (entry[1].length === 1) {
        httpParams = httpParams.append(entry[0], entry[1][0]);
      } else {
        entry[1].forEach((item) => {
          httpParams = httpParams.append(entry[0], item);
        });
      }
    });
    if (sessionStorage.getItem('MultiClientId') != null) {
      httpParams = httpParams.append(
        'MultiClientId',
        sessionStorage.getItem('MultiClientId')
      );
    }
    return httpParams;
  };

  public getHeaders(token: string, clientId?: number): HttpHeaders {
    let headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    if (clientId) {
      headers = headers.set('ClientId', clientId.toString());
    }

    if (this.featureFlagService.isActive('flagRDOApiAlloyDBHeader')) {
      headers = headers.set('X-Rouse-AlloyDB-Features', 'true');
    }

    return headers;
  }
}
